import React, { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Global } from '@emotion/react'
import { useTheme } from 'src/ui/style/useTheme'
import { useTran } from 'src/i18n/useTran'
import { msg } from 'src/i18n/texts/keys'
import { createSystemBannerStyle } from 'src/ui/style/SystemBannerStyle'

export type BannerType = 'idle-warning' | 'cleared' | 'error'
export type SystemBannerPayload = {
    type: BannerType
    title?: string
    message?: string
    etaMs?: number
    actions?: Array<{
        label: string
        onClick?: () => void
        variant?: 'primary' | 'ghost'
    }>
}

type Props = {
    banner: SystemBannerPayload | null
    onDismiss: () => void
}

function usePortalRoot(id: string) {
    const el = useMemo(() => {
        let node = document.getElementById(id) as HTMLDivElement | null
        if (!node) {
            node = document.createElement('div')
            node.id = id
            document.body.appendChild(node)
        }
        return node
    }, [id])

    useEffect(() => {
        return () => {
            const n = document.getElementById(id)
            if (n && n.childElementCount === 0) {
                n.remove()
            }
        }
    }, [id])

    return el
}

export default function SystemBannerPortal({ banner, onDismiss }: Props) {
    const root = usePortalRoot('system-banner-root')
    const th = useTheme()
    const t = useTran()
    const [leftMs, setLeftMs] = React.useState<number | null>(null)

    useEffect(() => {
        if (
            !banner ||
            banner.type !== 'idle-warning' ||
            typeof banner.etaMs !== 'number'
        ) {
            setLeftMs(null)
            return
        }
        const end = Date.now() + Math.max(0, banner.etaMs)
        const tick = () => setLeftMs(Math.max(0, end - Date.now()))
        tick()
        const id = setInterval(tick, 1000)
        return () => clearInterval(id)
    }, [banner?.type, banner?.etaMs])

    const fmt = (ms: number) => {
        const s = Math.floor(ms / 1000)
        const m = Math.floor(s / 60)
        const ss = s % 60
        return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    }

    useEffect(() => {
        if (!banner) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        document.body.dataset.bannerOpen = 'true'
        return () => {
            document.body.style.overflow = prev
            delete document.body.dataset.bannerOpen
        }
    }, [banner])

    if (!banner || !banner.type) return null

    const { title, message, type } = banner

    const defaults: Record<BannerType, { t: () => string; m: () => string }> = {
        'idle-warning': {
            t: () => t(msg.chatbot.chat.idleWarningTitle),
            m: () => t(msg.chatbot.chat.idleWarningAction)
        },
        cleared: {
            t: () => t(msg.chatbot.chat.clearedTitle),
            m: () => t(msg.chatbot.chat.clearedBecauseInactive)
        },
        error: {
            t: () => t(msg.error.unexpected),
            m: () => t(msg.chatbot.chat.error)
        }
    }

    const finalTitle = title ? t(title) : defaults[type].t()
    const finalMsg =
        message && t(message)
            ? t(message)
            : type === 'idle-warning' && leftMs !== null
            ? `${t(msg.chatbot.chat.idleWarningBody)} ${fmt(leftMs)}`
            : defaults[type].m()

    const onBackdropClick: React.MouseEventHandler<HTMLDivElement> = e => {
        if (e.target === e.currentTarget) onDismiss()
    }

    const confirm = banner.actions?.[0]
    const secondary = banner.actions?.[1]

    return createPortal(
        <>
            <Global styles={createSystemBannerStyle(th)} />
            <div
                className="sb-overlay"
                role="presentation"
                onClick={onBackdropClick}
            >
                <div
                    className={`sb-card sb-${type}`}
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="sb-title"
                    aria-describedby="sb-desc"
                    tabIndex={-1}
                >
                    <div className="sb-header">
                        <div className="sb-dot" />
                        <h2 id="sb-title" className="sb-title">
                            {finalTitle}
                        </h2>
                    </div>
                    <p id="sb-desc" className="sb-message">
                        {finalMsg}
                    </p>
                    <div className="sb-actions">
                        {secondary ? (
                            <button
                                className="sb-btn sb-btn-ghost"
                                onClick={() => {
                                    secondary.onClick?.()
                                    onDismiss()
                                }}
                            >
                                {secondary.label}
                            </button>
                        ) : null}
                        {confirm ? (
                            <button
                                className="sb-btn sb-btn-primary"
                                onClick={() => {
                                    confirm.onClick?.()
                                    onDismiss()
                                }}
                            >
                                {confirm.label}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </>,
        root
    )
}
